import { type FC, lazy, Suspense } from "react";

import { SectionHeading } from "@clientFrontend/features/scout/components/SectionHeading";
import { InformationBox } from "@clientFrontend/features/scout/components/InformationBox";

import { ReadMoreButton } from "@root/shared/components/ReadMoreButton";
import { retryLazyLoad } from "@root/shared/utils/retryLazyLoad";
import { PhotoContestLabel } from "@clientFrontend/components/PhotoContestLabel";
import { type PhotoContestInfo } from "@clientFrontend/features/scout/types/photoContestInfo";
import {
  BasicHopedConditionsTable,
  type basicHopedConditionsProps,
} from "./uiParts/BasicHopedConditionsTable";
import {
  ThoughtsAndSelfPrTable,
  type thoughtsAndSelfPrProps,
} from "./uiParts/ThoughtsAndSelfPrTable";
import {
  DetailHopedConditionsTable,
  type detailHopedConditionsProps,
} from "./uiParts/DetailHopedConditionsTable";
import { type userProductPhotoProps } from "./uiParts/UserProductPhotosCassette";
import { UserSkillsList, type userSkillsProps } from "./uiParts/UserSkillsList";
import { UserCareerDefinitionList } from "./uiParts/UserCareerDefinitionList";
import { UserAchievementsDefinitionList } from "./uiParts/UserAchievementsDefinitionList";
import { UserSchoolsDefinitionList } from "./uiParts/UserSchoolsDefinitionList";
import {
  ActivityHistoriesTable,
  type ActivityHistoriesProps,
} from "./uiParts/ActivityHistoriesTable";
import {
  MultiActionInformationBox,
  type MultiActionItem,
  type MinOneArray,
} from "./uiParts/MultiActionInformationBox";
import { AlertTextPanel } from "./uiParts/AlertTextPanel";

import * as styles from "./styles.css";

const UserProductPhotosCassette = lazy(() =>
  retryLazyLoad({
    fn: () =>
      import("@clientFrontend/features/scout/components/UserDetail/uiParts/UserProductPhotosCassette"),
  }),
);

type UserDetailProps = {
  displayControlInformation: {
    isHideInfo?: boolean;
    hideInfoReasonText?: string;
    notScoutableReasonText?: string;
    isNotScoutable: boolean;
    scoutIndexUrl?: string;
    isNew: boolean;
    entryText?: string;
    isIconFire: boolean;
  };
  userData: {
    sexAndAgeText: string;
    acceptableReceivedScoutCount?: number;
    currentAccessAt?: string;
    multiActionInfo?: MultiActionItem[];
    photoContestInfo?: PhotoContestInfo[];
  };
  historyInformation: {
    userSchoolInfoList?: { term: string; description: string | string[] }[][];
    userCareerInfoList?: { term: string; description: string | string[] }[][];
    userAchievementsInfoList?: { term: string; description: string | string[] }[][];
    activityHistories?: ActivityHistoriesProps[];
  };
  hopedConditions: {
    basicHopedConditions?: basicHopedConditionsProps;
    detailHopedConditions?: detailHopedConditionsProps;
  };
  userProfileInformation: {
    thoughtsAndSelfPr?: thoughtsAndSelfPrProps;
    userSkills?: userSkillsProps[];
    userProductPhotos?: userProductPhotoProps[];
  };
  showReadMoreButtonControlInformation: {
    showReadMoreButton?: boolean;
    maxDisplayLargeTextHeight?: number;
    maxDisplaySmallTextHeight?: number;
  };
};

// スキル・経験業務
// 要素の高さが160px以上
const USER_SKILLS_MAX_SIZE = 160;

export const UserDetail: FC<UserDetailProps> = ({
  displayControlInformation,
  userData,
  historyInformation,
  hopedConditions,
  userProfileInformation,
  showReadMoreButtonControlInformation: {
    showReadMoreButton = false,
    maxDisplayLargeTextHeight = 0,
    maxDisplaySmallTextHeight = 0,
  },
}) => {
  return (
    <>
      {displayControlInformation.isNotScoutable &&
        displayControlInformation.notScoutableReasonText &&
        displayControlInformation.scoutIndexUrl && (
          <AlertTextPanel
            reasonText={displayControlInformation.notScoutableReasonText}
            scoutIndexUrl={displayControlInformation.scoutIndexUrl}
          />
        )}
      <p className={styles.basicInfoText}>
        他社からのスカウト受信数:{userData.acceptableReceivedScoutCount}件<br />
        最終アクセス日:{userData.currentAccessAt}
      </p>
      {userData.multiActionInfo && userData.multiActionInfo.length > 0 && (
        <MultiActionInformationBox
          multiActionInfo={userData.multiActionInfo as MinOneArray<MultiActionItem>}
          isIconFire={displayControlInformation.isIconFire}
          showReadMoreButton={showReadMoreButton}
        />
      )}
      {userData.photoContestInfo && userData.photoContestInfo.length > 0 && (
        <div className={styles.photoContestLabelList}>
          {userData.photoContestInfo.map((info) => (
            <div key={info.title} className={styles.photoContestLabelItem}>
              <PhotoContestLabel rank={info.rank} title={info.title} isSp />
            </div>
          ))}
        </div>
      )}
      {hopedConditions.basicHopedConditions && (
        <section className={styles.contentsMarginMedium}>
          <BasicHopedConditionsTable basicHopedConditions={hopedConditions.basicHopedConditions} />
        </section>
      )}
      {hopedConditions.detailHopedConditions && (
        <section className={styles.sectionContainer}>
          <SectionHeading>希望条件の詳細</SectionHeading>
          <div className={styles.contentsMarginSmall}>
            <DetailHopedConditionsTable
              detailHopedConditions={hopedConditions.detailHopedConditions}
              maxDisplayLargeTextHeight={maxDisplayLargeTextHeight}
              maxDisplaySmallTextHeight={maxDisplaySmallTextHeight}
              showReadMoreButton={showReadMoreButton}
            />
          </div>
        </section>
      )}
      {historyInformation.userSchoolInfoList &&
        historyInformation.userSchoolInfoList.length > 0 && (
          <section className={styles.listSectionContainer}>
            <SectionHeading>学歴</SectionHeading>
            <ReadMoreButton size="large">
              <UserSchoolsDefinitionList
                userSchoolInfoList={historyInformation.userSchoolInfoList}
              />
            </ReadMoreButton>
          </section>
        )}
      {historyInformation.userCareerInfoList &&
        historyInformation.userCareerInfoList.length > 0 && (
          <section className={styles.listSectionContainer}>
            <SectionHeading>職務経歴</SectionHeading>
            <ReadMoreButton size="large">
              <UserCareerDefinitionList
                userCareerInfoList={historyInformation.userCareerInfoList}
              />
            </ReadMoreButton>
          </section>
        )}
      {userProfileInformation.userSkills && (
        <section className={styles.sectionContainer}>
          <SectionHeading>スキル・経験業務</SectionHeading>
          <div className={styles.contentsMarginSmall}>
            <ReadMoreButton size="large">
              <UserSkillsList
                userSkills={userProfileInformation.userSkills}
                maxSize={USER_SKILLS_MAX_SIZE}
              />
            </ReadMoreButton>
          </div>
        </section>
      )}
      {userProfileInformation.userProductPhotos &&
        userProfileInformation.userProductPhotos.length > 0 && (
          <section className={styles.sectionContainer}>
            <SectionHeading>求職者を表現する画像</SectionHeading>
            <ReadMoreButton size="large">
              <Suspense fallback={null}>
                <UserProductPhotosCassette
                  userProductPhotos={userProfileInformation.userProductPhotos}
                />
              </Suspense>
            </ReadMoreButton>
          </section>
        )}
      {historyInformation.userAchievementsInfoList &&
        historyInformation.userAchievementsInfoList.length > 0 && (
          <section className={styles.listSectionContainer}>
            <SectionHeading>受賞・表彰歴</SectionHeading>
            <ReadMoreButton size="large">
              <UserAchievementsDefinitionList
                userAchievementsInfoList={historyInformation.userAchievementsInfoList}
              />
            </ReadMoreButton>
          </section>
        )}
      {userProfileInformation.thoughtsAndSelfPr && (
        <section className={styles.sectionContainer}>
          <SectionHeading>想い・自己PR</SectionHeading>
          <ThoughtsAndSelfPrTable
            thoughtsAndSelfPr={userProfileInformation.thoughtsAndSelfPr}
            maxDisplayLargeTextHeight={maxDisplayLargeTextHeight}
            showReadMoreButton={showReadMoreButton}
          />
        </section>
      )}
      <section className={styles.sectionContainer}>
        <SectionHeading>活動履歴</SectionHeading>
        {historyInformation.activityHistories ? (
          <ReadMoreButton size="large">
            <ActivityHistoriesTable activityHistories={historyInformation.activityHistories} />
          </ReadMoreButton>
        ) : (
          <InformationBox className={styles.activityHistoryInformationBox}>
            <p>履歴はありません</p>
          </InformationBox>
        )}
      </section>
    </>
  );
};
